import { spaceAPI } from '@/api';
import {
  Button,
  ButtonGroup,
  Container,
  Content,
  DeleteButton,
  HeaderContainer,
  LoadingMessage,
  Modal,
  ModalButtons,
  ModalContent,
  SearchContainer,
  SearchInput,
  SectionContainer,
  SectionContent,
  SectionHeader,
  SectionSubTitle,
  SectionTitle,
  SectionWrapper,
  SelectionCount,
  Table,
  TableContainer,
} from '@/components/CommonStyles';
import HeaderComponent from './ResomHeader';
import { useQuery } from '@tanstack/react-query';
import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
  getSortedRowModel,
  SortingState,
  getFilteredRowModel,
} from '@tanstack/react-table';
import type {
  ColumnDef,
  Row,
  Header,
  Cell,
  HeaderGroup,
} from '@tanstack/react-table';
import styled from 'styled-components';
import { palette } from '@/modules/defines/styles';

interface UserData {
  id: number;
  uid: string;
  isActivate: boolean;
  phone: string;
  room: string;
  createdAt: string;
}

const ResomDataContainer = () => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  // const navigate = useNavigate();
  const columnHelper = createColumnHelper<UserData>();

  const { data: dataAPI, isLoading: isLoadingAPI } = useQuery({
    queryKey: ['getUsersResom'],
    queryFn: () =>
      spaceAPI.getUsersResom({
        code: import.meta.env.VITE_SECRET_API_KEY,
      }),
    staleTime: 1000 * 60 * 5,
    enabled: true,
  });

  const toggleRowSelection = (id: number) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
  };

  const columns = useMemo(() => {
    const baseColumns = [
      isEditMode
        ? columnHelper.display({
            id: 'checkbox',
            header: () => (
              <input
                type="checkbox"
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedRows(
                      dataAPI?.data?.map((item: UserData) => item.id) || []
                    );
                  } else {
                    setSelectedRows([]);
                  }
                }}
                checked={
                  selectedRows.length > 0 &&
                  selectedRows.length === (dataAPI?.data?.length || 0)
                }
              />
            ),
            cell: (info) => (
              <input
                type="checkbox"
                onChange={() => toggleRowSelection(info.row.original.id)}
                checked={selectedRows.includes(info.row.original.id)}
                onClick={(e) => e.stopPropagation()}
              />
            ),
          })
        : null,
      columnHelper.accessor('id', {
        header: 'ID',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('uid', {
        header: 'UID',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('isActivate', {
        header: '활성화 상태',
        cell: (info) => (info.getValue() ? '활성화' : '비활성화'),
      }),
      columnHelper.accessor('phone', {
        header: '전화번호',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('room', {
        header: '객실',
        cell: (info) => {
          const room = info.getValue() ? info.getValue().split('_') : null;
          if (!room) return '-';
          if (room[0] === 'restree') {
            return `레스트리 ${room[1]}호`;
          } else if (room[0] === 'forest') {
            return `빌라동 ${room[1]}`;
          } else {
            return '-';
          }
        },
      }),
      columnHelper.accessor('createdAt', {
        header: '생성일',
        cell: (info) => new Date(info.getValue()).toLocaleDateString(),
      }),
    ];

    return baseColumns.filter(Boolean) as ColumnDef<UserData, any>[];
  }, [isEditMode, selectedRows, dataAPI?.data]);

  const table = useReactTable({
    data: dataAPI?.data || [],
    columns,
    state: {
      sorting,
      globalFilter: searchTerm,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onGlobalFilterChange: setSearchTerm,
    globalFilterFn: (row, columnId, filterValue) => {
      const value = String(row.getValue(columnId) || '').toLowerCase();
      return value.includes(String(filterValue).toLowerCase());
    },
  });

  const handleEditMode = () => {
    setIsEditMode(true);
    setSelectedRows([]);
  };

  const handleCancelEditMode = () => {
    setIsEditMode(false);
    setSelectedRows([]);
  };

  // 유저 삭제 LOGIC
  const handleDelete = () => {
    if (selectedRows.length === 0) {
      alert('삭제할 항목을 선택해주세요.');
      return;
    }
    setShowDeleteModal(true);
  };

  // 유저 삭제 확인 LOGIC
  const confirmDelete = () => {
    alert('유저 삭제 기능은 아직 구현되지 않았습니다.');
    // 여기에 유저 삭제 로직 추가

    setShowDeleteModal(false);
    setSelectedRows([]);
  };

  return (
    <Container>
      <HeaderComponent />
      <Content>
        {isLoadingAPI ? (
          <LoadingMessage>데이터를 불러오는 중입니다...</LoadingMessage>
        ) : !dataAPI?.data ? (
          <LoadingMessage>
            데이터가 없습니다. 잠시 후 다시 시도해주세요.
          </LoadingMessage>
        ) : (
          <SectionWrapper>
            <SectionContainer>
              <SectionHeader>
                <SectionTitle>유저</SectionTitle>
                <SectionSubTitle>총 {dataAPI?.data?.length}명</SectionSubTitle>
              </SectionHeader>
              <HeaderContainer>
                <SearchContainer>
                  <SearchInput
                    type="text"
                    placeholder="검색어를 입력하세요..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </SearchContainer>
                <ButtonGroup>
                  {isEditMode ? (
                    <>
                      {selectedRows.length > 0 && (
                        <SelectionCount>
                          총 {selectedRows.length}개 선택
                        </SelectionCount>
                      )}
                      <Button onClick={handleCancelEditMode}>취소</Button>
                      <DeleteButton onClick={handleDelete}>삭제</DeleteButton>
                    </>
                  ) : (
                    <>
                      <Button onClick={handleEditMode}>수정</Button>
                    </>
                  )}
                </ButtonGroup>
              </HeaderContainer>
              <TableContainer maxHeight="200px">
                <Table>
                  <thead>
                    {table
                      .getHeaderGroups()
                      .map((headerGroup: HeaderGroup<UserData>) => (
                        <tr key={headerGroup.id}>
                          {headerGroup.headers.map(
                            (header: Header<UserData, unknown>) => (
                              <th
                                key={header.id}
                                onClick={header.column.getToggleSortingHandler()}
                                style={{ cursor: 'pointer' }}
                              >
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                                {{
                                  asc: ' 🔼',
                                  desc: ' 🔽',
                                }[header.column.getIsSorted() as string] ??
                                  null}
                              </th>
                            )
                          )}
                        </tr>
                      ))}
                  </thead>
                  <tbody>
                    {table.getRowModel().rows.map((row: Row<UserData>) => (
                      <tr
                        key={row.id}
                        onClick={
                          isEditMode
                            ? () => toggleRowSelection(row.original.id)
                            : () => {
                                // navigate(`/resom/user/${row.original.id}`);
                                console.log(row.original.id);
                              }
                        }
                        style={{ cursor: 'pointer' }}
                      >
                        {row
                          .getVisibleCells()
                          .map((cell: Cell<UserData, unknown>) => (
                            <td key={cell.id}>
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </td>
                          ))}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableContainer>
            </SectionContainer>
            <SectionContainer>
              <SectionHeader>
                <SectionTitle>사용 통계</SectionTitle>
              </SectionHeader>
              <SectionContent>
                <Button
                  onClick={() => {
                    window.open(
                      'https://app.amplitude.com/analytics/share/b1f58d283b2c443fbd6eec007b3f7ec5',
                      '_blank'
                    );
                  }}
                >
                  조회수/페이지뷰
                </Button>
              </SectionContent>
            </SectionContainer>
          </SectionWrapper>
        )}
        {showDeleteModal && (
          <Modal>
            <ModalContent>
              <p>{selectedRows.length}개의 항목이 선택되었습니다.</p>
              <h3>삭제하신 유저는 다시 복구할 수 없습니다.</h3>
              <h3>그래도 삭제하시겠습니까?</h3>
              <ModalButtons>
                <Button onClick={() => setShowDeleteModal(false)}>취소</Button>
                <DeleteButton onClick={confirmDelete}>삭제</DeleteButton>
              </ModalButtons>
            </ModalContent>
          </Modal>
        )}
      </Content>
    </Container>
  );
};

export default ResomDataContainer;
